import PlatformIcon from './PlatformIcon';
import { getPlatformColor, getPlatformLabel } from '@/utils/platform';
import { formatCompact } from '@/utils/format';

export interface PlatformAudience {
  platform: string;
  followers: number;
}

interface PlatformAudienceSplitProps {
  platforms: PlatformAudience[];
  /** Tighter bar + condensed legend, for narrow columns such as the compare view. */
  compact?: boolean;
  className?: string;
}

export default function PlatformAudienceSplit({
  platforms,
  compact = false,
  className = '',
}: PlatformAudienceSplitProps) {
  const withFollowers = platforms.filter((entry) => entry.followers > 0);
  const total = withFollowers.reduce((sum, entry) => sum + entry.followers, 0);

  if (total <= 0) {
    return null;
  }

  const sorted = [...withFollowers].sort((a, b) => b.followers - a.followers);

  return (
    <div className={className}>
      <div
        className={`flex w-full gap-[2px] overflow-hidden rounded-full bg-background-200 ${
          compact ? 'h-1.5' : 'h-2.5'
        }`}
        role="img"
        aria-label={`Audience split across ${sorted.length} platforms`}
      >
        {sorted.map((entry) => {
          const share = (entry.followers / total) * 100;
          return (
            <span
              key={entry.platform}
              className="h-full rounded-[2px]"
              style={{
                flexGrow: entry.followers,
                flexBasis: 0,
                backgroundColor: getPlatformColor(entry.platform),
              }}
              title={`${getPlatformLabel(entry.platform)} · ${formatCompact(entry.followers)} followers (${share.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      {compact ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {sorted.map((entry) => {
            const share = (entry.followers / total) * 100;
            return (
              <span
                key={entry.platform}
                className="inline-flex items-center gap-1 text-[11px] text-foreground-600"
              >
                <PlatformIcon platform={entry.platform} className="text-[12px]" />
                <span className="font-label font-medium text-foreground-800">
                  {share.toFixed(0)}%
                </span>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2.5">
          {sorted.map((entry) => {
            const share = (entry.followers / total) * 100;
            return (
              <span key={entry.platform} className="inline-flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md border border-background-200 bg-background-50 text-[12px]">
                  <PlatformIcon platform={entry.platform} className="text-[12px]" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-xs text-foreground-700">
                    {getPlatformLabel(entry.platform)}
                  </span>
                  <span className="text-[11px] text-foreground-400">
                    {formatCompact(entry.followers)} · {share.toFixed(0)}%
                  </span>
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}